import { TERRITORY_ZONES } from "@/data/territories";
import { getTerritoryLabel, REGION_OPTIONS } from "@/data/territory-display";

/**
 * Territory presets: industry-named groups a buyer picks in one click (DEC-070, spec §3.4).
 *
 * NOT regions. The eight regions in `territories.ts` are a disjoint partition the backend collapses
 * with (`territory_service.REGION_COUNTRIES`, pinned by an anti-drift test), so they cannot overlap.
 * Industry groups do overlap — Mexico is both North American and Latin American — so a preset is a
 * plain list of country codes. Picking one stores those codes, exactly as picking countries does;
 * nothing new reaches the backend. A stored set reads back by the presets it holds whole
 * (`decomposeTerritoryPresets`).
 */
export interface TerritoryPreset {
  id: string;
  label: string;
  codes: readonly string[];
}

const zoneCodes = (zone: keyof typeof TERRITORY_ZONES) => TERRITORY_ZONES[zone].map((t) => t.code);

export const TERRITORY_PRESETS: readonly TerritoryPreset[] = [
  { id: "uk_ie", label: "UK & Ireland", codes: ["GB", "GG", "JE", "IM", "IE"] },
  { id: "north_america", label: "North America", codes: ["US", "CA", "MX"] },
  // DACH and GSA name the same three countries until Believe says otherwise (spec A3).
  { id: "dach", label: "DACH", codes: ["DE", "AT", "CH"] },
  // Industry LatAm includes South America; our regions split it in two (spec A4).
  { id: "latam", label: "LATAM", codes: [...zoneCodes("LATAM"), ...zoneCodes("SA")] },
];

/** Largest first, so where two presets share a country (Mexico) the larger one keeps it. */
const BY_SIZE = [...TERRITORY_PRESETS].sort((a, b) => b.codes.length - a.codes.length);

/**
 * The presets a stored territory set holds WHOLE, and what is left over.
 *
 *   GB GG JE IM IE                 -> [UK & Ireland], []
 *   GB GG JE IM IE US CA MX        -> [UK & Ireland, North America], []
 *   GB GG JE IM IE FR              -> [UK & Ireland], [FR]
 *   DE AT CH IT FR                 -> [DACH], [IT, FR]
 *   DE AT                          -> [], [DE, AT]          (a preset is named only when whole)
 *
 * Greedy, largest preset first; a country counts toward one preset only. A preset is NOT taken out
 * of a whole region ("Europe" stays "Europe", never "DACH" + 49 countries) unless that region lies
 * wholly inside the preset (the UK in "UK & Ireland", both Latin regions in "LATAM"). Case-insensitive; `rest` keeps each leftover token as given (a region token, a typo),
 * in the order given, once. Presets come back in `TERRITORY_PRESETS` order.
 */
export function decomposeTerritoryPresets(tokens: readonly string[]): {
  presets: TerritoryPreset[];
  rest: string[];
} {
  const remaining = new Set(tokens.map((token) => token.trim().toUpperCase()).filter(Boolean));
  const wholeRegions = REGION_OPTIONS.filter((region) => region.countries.every((code) => remaining.has(code)));
  const taken = new Set<TerritoryPreset>();
  for (const preset of BY_SIZE) {
    if (!preset.codes.every((code) => remaining.has(code))) continue;
    const breaksARegion = wholeRegions.some(
      (region) =>
        region.countries.some((code) => preset.codes.includes(code)) &&
        !region.countries.every((code) => preset.codes.includes(code)),
    );
    if (breaksARegion) continue;
    taken.add(preset);
    preset.codes.forEach((code) => remaining.delete(code));
  }
  const rest: string[] = [];
  const seen = new Set<string>();
  for (const token of tokens) {
    const key = token.trim().toUpperCase();
    if (!key || seen.has(key) || !remaining.has(key)) continue;
    seen.add(key);
    rest.push(token.trim());
  }
  return { presets: TERRITORY_PRESETS.filter((preset) => taken.has(preset)), rest };
}

/**
 * One label per stored token, with the presets it holds whole named once ("UK & Ireland", "France").
 * For the surfaces that list a territory token by token rather than folding regions
 * (`LicenseTermsSummary`, the request page's coverage line); `territoryLabels` in
 * `lib/territory-scope.ts` is the folding reader.
 */
export function presetTerritoryLabels(tokens: readonly string[] | null | undefined): string[] {
  const { presets, rest } = decomposeTerritoryPresets(tokens ?? []);
  return [...presets.map((preset) => preset.label), ...rest.map(getTerritoryLabel)];
}
